import { z } from 'zod';
import { crowdexNativeAgentSchema } from './request-schemas';

/** Largest native history, base64-encoded, that a handoff carries (about 12 MB of JSONL). */
export const MAX_AGENT_SESSION_HISTORY_BASE64 = 16 * 1024 * 1024;

/**
 * A native agent session moved between two Crowdex workspace servers (this
 * machine and a cloud sandbox run the same one). It is the provider's session
 * id plus its own history file, which the destination writes where the same
 * provider looks for it, for its own workspace path. Providers whose history
 * cannot be moved (ACP agents, OpenCode) are never exported; the destination
 * then rebuilds the session from the canonical Crowdex transcript.
 */
export const agentSessionTransferSchema = z
  .object({
    provider: z.enum(['claude-code', 'codex']),
    sessionId: z.string().regex(/^[a-zA-Z0-9._-]{1,128}$/),
    history: z
      .object({
        /** File name in the provider's store, kept so the destination can recognize it. */
        fileName: z.string().regex(/^[a-zA-Z0-9._-]{1,255}\.jsonl$/),
        data: z.string().max(MAX_AGENT_SESSION_HISTORY_BASE64),
      })
      .strict(),
  })
  .strict();

export type AgentSessionTransfer = z.infer<typeof agentSessionTransferSchema>;

/** The conversation whose session a workspace server exports or imports. */
const agentSessionTargetSchema = z
  .object({
    conversationId: z.string().min(1).max(128),
    agent: crowdexNativeAgentSchema,
    workspaceRoot: z.string().min(1).max(4096),
  })
  .strict();

/** `POST /v1/agent/sessions/export`: stops the conversation's session and returns it. */
export const agentSessionExportRequestSchema = agentSessionTargetSchema;

export const agentSessionExportResponseSchema = z.object({
  session: agentSessionTransferSchema.nullable(),
});

/** `POST /v1/agent/sessions/import`: the next turn of the conversation resumes this session. */
export const agentSessionImportRequestSchema = agentSessionTargetSchema.extend({
  session: agentSessionTransferSchema,
});

export const agentSessionImportResponseSchema = z.object({ imported: z.boolean() });

export type AgentSessionExportRequest = z.infer<typeof agentSessionExportRequestSchema>;
export type AgentSessionImportRequest = z.infer<typeof agentSessionImportRequestSchema>;
